import React, { useState, useEffect } from 'react';
import { agentService } from '../API/agentService';

const ReportsViewer = ({ projects, agents }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('week');
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    loadReports();
  }, [timeframe, selectedProject]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const reportsData = await agentService.getReports(
        timeframe,
        selectedProject === 'all' ? null : selectedProject
      );
      setReports(reportsData);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = reports.filter(report => {
    if (selectedType === 'all') return true;
    return report.type === selectedType;
  });

  const getReportTypeIcon = (type) => {
    const icons = {
      standup: '📊',
      review: '🔍',
      progress: '📈',
      general: '📄'
    };
    return icons[type] || '📄';
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVelocityTrend = () => {
    if (!projects.length) return { trend: 'stable', value: 0 };

    const avgVelocity = projects.reduce((sum, p) => sum + (p.velocityTarget || 0), 0) / projects.length;
    // Mock trend calculation - in real implementation would compare with historical data
    return {
      trend: avgVelocity > 6 ? 'up' : avgVelocity > 4 ? 'stable' : 'down',
      value: Math.round(avgVelocity * 10) / 10
    };
  };

  const getCompletionTrend = () => {
    if (!projects.length) return { trend: 'stable', value: 0 };

    const avgCompletion = projects.reduce((sum, p) => sum + (p.completionPercentage || 0), 0) / projects.length;
    // Mock trend calculation
    return {
      trend: avgCompletion > 60 ? 'up' : avgCompletion > 30 ? 'stable' : 'down',
      value: Math.round(avgCompletion)
    };
  };

  const getTrendIcon = (trend) => {
    const icons = {
      up: '📈',
      down: '📉',
      stable: '➡️'
    };
    return icons[trend] || '➡️';
  };

  return (
    <div className="reports-viewer">
      {/* Filters */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Reports & Analytics</h3>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Timeframe:</label>
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
            >
              <option value="day">Last 24 Hours</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Project:</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Type:</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="standup">Daily Standups</option>
              <option value="review">Code Reviews</option>
              <option value="progress">Progress Reports</option>
              <option value="general">General Reports</option>
            </select>
          </div>

          <button className="btn btn-secondary" onClick={loadReports}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-3">
        <div className="dashboard-card">
          <div className="metric-content">
            <div className="metric-header">
              <span className="metric-icon">⚡</span>
              <span className="metric-label">Velocity Trend</span>
            </div>
            <div className="metric-value">
              {getTrendIcon(getVelocityTrend().trend)} {getVelocityTrend().value} pts/day
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="metric-content">
            <div className="metric-header">
              <span className="metric-icon">🎯</span>
              <span className="metric-label">Completion Trend</span>
            </div>
            <div className="metric-value">
              {getTrendIcon(getCompletionTrend().trend)} {getCompletionTrend().value}%
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="metric-content">
            <div className="metric-header">
              <span className="metric-icon">🤖</span>
              <span className="metric-label">Agent Activity</span>
            </div>
            <div className="metric-value">
              📊 {filteredReports.length} reports
            </div>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Recent Reports</h3>
          <p className="card-subtitle">
            {selectedProject === 'all' ? 'All projects' : projects.find(p => p.id === selectedProject)?.name}
            • {timeframe === 'day' ? 'Last 24 hours' : timeframe === 'week' ? 'Last week' : 'Last month'}
          </p>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading reports...</p>
          </div>
        ) : (
          <div className="reports-list">
            {filteredReports.length > 0 ? (
              filteredReports.map(report => (
                <div key={report.id} className="report-item">
                  <div className="report-header">
                    <div className="report-info">
                      <span className="report-icon">{getReportTypeIcon(report.type)}</span>
                      <div>
                        <h4 className="report-title">
                          {report.type.charAt(0).toUpperCase() + report.type.slice(1)} Report
                        </h4>
                        <div className="report-meta">
                          <span className="report-project">{report.project}</span>
                          <span className="report-agent">by {report.agent}</span>
                          <span className="report-date">{formatDate(report.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                    <button className="btn btn-secondary">
                      📄 View Full Report
                    </button>
                  </div>

                  <div className="report-preview">
                    {report.content ? (
                      <>
                        {report.content.split('\n').slice(0, 3).map((line, index) => (
                          <p key={index}>{line}</p>
                        ))}
                        {report.content.split('\n').length > 3 && (
                          <p className="report-more">...</p>
                        )}
                      </>
                    ) : (
                      <p>{report.summary || 'No content available'}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <h4>No reports found</h4>
                <p>
                  {selectedProject === 'all'
                    ? `No ${selectedType === 'all' ? '' : selectedType} reports found for the selected timeframe.`
                    : `No reports found for ${projects.find(p => p.id === selectedProject)?.name} in the selected timeframe.`
                  }
                </p>
                <button className="btn btn-primary" onClick={loadReports}>
                  🔄 Check Again
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsViewer;